import React, { useMemo } from 'react';
import ProductPageHeader from './components/ProductPageHeader';
import ProductTable from './components/ProductTable';
import ProductFormModal from '../../components/shared/ProductFormModal';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import BulkActionsBar, { BulkAction } from '../../components/shared/BulkActionsBar';
import { TrashIcon } from '../../components/icons';
import ProductFilterModal from './components/ProductFilterModal';
import { useProductsPage } from './hooks/useProductsPage';
import Pagination from '../../components/shared/Pagination';
import { PageHeader } from '../../components/layout/Header';

const ProductsFeature: React.FC = () => {
    const {
        filteredProducts,
        paginatedProducts,
        loading,
        refreshProducts,
        currentPage,
        setCurrentPage,
        pageSize,
        selectedIds,
        isAllSelected,
        isSomeSelected,
        toggleSelection,
        toggleAll,
        clearSelection,
        searchValue,
        setSearchValue,
        hasActiveFilters,
        filterValues,
        modals,
        handleSaveProduct,
        handleConfirmDelete,
        handleBulkDelete,
        handleDuplicateProduct,
        handleExport,
        handleImport,
        handleApplyFilters
    } = useProductsPage();

    const bulkActions: BulkAction[] = useMemo(() => [
        {
            label: 'Excluir',
            icon: TrashIcon,
            onClick: () => modals.setBulkDeleteModalOpen(true),
            variant: 'danger' as const
        }
    ], [modals]);

    return (
        <>
            <div className="flex h-full flex-col">
                <PageHeader
                    title="Produtos"
                    subtitle="Gerencie seu catálogo de produtos."
                    className="mb-0 gap-s3 md:mb-6 md:gap-3"
                    titleClassName="text-lg md:text-xl"
                    actionClassName="w-full md:w-auto"
                    hideBackButton
                    action={
                        <ProductPageHeader
                            onAddProduct={modals.openCreateModal}
                            onExport={handleExport}
                            onImport={handleImport}
                            searchValue={searchValue}
                            onSearchChange={setSearchValue}
                            onOpenFilters={() => modals.setFilterModalOpen(true)}
                            hasActiveFilters={hasActiveFilters}
                            onRefresh={refreshProducts}
                        />
                    }
                />

                <section aria-label="Lista de produtos" className="min-h-0 flex-1 overflow-x-auto pt-s3 md:pt-0">
                    <ProductTable
                        products={paginatedProducts}
                        isLoading={loading}
                        onEdit={modals.openEditModal}
                        onDelete={modals.openDeleteModal}
                        onDuplicate={handleDuplicateProduct}
                        showCheckbox={true}
                        selectedIds={selectedIds}
                        allSelected={isAllSelected}
                        someSelected={isSomeSelected}
                        onToggleSelection={toggleSelection}
                        onToggleAll={toggleAll}
                        footer={
                            <Pagination
                                currentPage={currentPage}
                                totalCount={filteredProducts.length}
                                pageSize={pageSize}
                                onPageChange={setCurrentPage}
                                selectedCount={selectedIds.size}
                            />
                        }
                    />
                </section>
            </div>

            <BulkActionsBar
                selectedCount={selectedIds.size}
                actions={bulkActions}
                onClose={clearSelection}
            />

            <ProductFormModal
                isOpen={modals.isFormModalOpen}
                onClose={modals.closeModals}
                onSave={handleSaveProduct}
                product={modals.productToEdit}
            />

            <ConfirmationModal
                isOpen={modals.isDeleteModalOpen}
                onClose={modals.closeModals}
                onConfirm={handleConfirmDelete}
                title="Excluir Produto"
                message={`Tem certeza que deseja excluir o produto "${modals.productToDelete?.name}"? Esta ação não pode ser desfeita.`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <ConfirmationModal
                isOpen={modals.isBulkDeleteModalOpen}
                onClose={() => modals.setBulkDeleteModalOpen(false)}
                onConfirm={handleBulkDelete}
                title="Excluir Produtos"
                message={`Tem certeza que deseja excluir ${selectedIds.size} produto(s)? Esta ação não pode ser desfeita.`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <ProductFilterModal
                isOpen={modals.isFilterModalOpen}
                onClose={modals.closeModals}
                onApply={handleApplyFilters}
                initialName={filterValues.name}
                initialOperator={filterValues.operator}
                initialMinValue={filterValues.min}
                initialMaxValue={filterValues.max}
            />
        </>
    );
};

export default ProductsFeature;
